import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Search, Filter, Globe } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function JobSearchPage() {
  return (
    <>
      <Helmet>
        <title>Job Search - Search 1M+ Jobs Across All Platforms | LoopBaan.ai</title>
        <meta name="description" content="Search over 1 million jobs from Indeed, LinkedIn, ZipRecruiter, and more in one place. Find your perfect role faster." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-accent/10 via-background to-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-accent/20 text-accent border-accent/30">FREE + 1M+ JOBS</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Search Every Job Platform{" "}
                <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                  In One Place
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Access over 1 million jobs from Indeed, LinkedIn, ZipRecruiter, Glassdoor, and more. Free tier with credit metering - 1 credit per 10 results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Start Searching <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Search,
                  title: "Unified Search",
                  description: "Search across all major job boards at once. Available on Free tier - 1 credit per page of 10 results."
                },
                {
                  icon: Filter,
                  title: "Advanced Filters",
                  description: "Filter by salary, location, experience level, company size, remote options, and more."
                },
                {
                  icon: Globe,
                  title: "Global Reach",
                  description: "Find opportunities anywhere in the world. Remote, hybrid, or in-office positions in any location."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Why Use Our Job Search?</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "1M+ jobs from all major platforms",
                  "Real-time job postings",
                  "Free tier - 1 credit per page (10 results)",
                  "Cache prevents double charging on refresh",
                  "Save searches and get alerts",
                  "One-click apply to saved jobs",
                  "AI-powered job recommendations",
                  "Company insights and reviews",
                  "Salary transparency",
                  "Track applications automatically"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Find Your Dream Job Today
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Start searching millions of jobs from every major platform. Free tier includes 10 credits/month - that's 100 job results!
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Searching <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
